import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { formatBytes } from '../utils/imageOps';
import type { ConvertedFile } from '../types';

const { FiDownload, FiDownloadCloud } = FiIcons;

interface ResultCardProps {
  item: ConvertedFile;
  onDownload: (item: ConvertedFile) => void | Promise<void>;
  index: number;
}

const ResultCard = ({ item, onDownload, index }: ResultCardProps) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: index * 0.04 }}
    className="group flex items-center justify-between gap-3 p-3 bg-white/60 dark:bg-slate-800/60 rounded-2xl border border-slate-100 dark:border-slate-700 hover:border-indigo-300 dark:hover:border-indigo-700 hover:shadow-md transition-[border-color,box-shadow,background-color]"
  >
    <div className="flex items-center gap-3 min-w-0">
      {/* Thumbnail */}
      <div className="w-12 h-12 shrink-0 rounded-xl overflow-hidden bg-slate-100 dark:bg-slate-700 border border-slate-200 dark:border-slate-600">
        <img
          src={item.url}
          alt={item.name}
          width={48}
          height={48}
          className="w-full h-full object-cover"
          loading="lazy"
        />
      </div>
      <div className="min-w-0">
        <p className="text-sm font-semibold truncate text-slate-800 dark:text-slate-100" title={item.name}>
          {item.name}
        </p>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400">
            {item.ext}
          </span>
          <span className="text-[10px] text-slate-400">{formatBytes(item.size)}</span>
        </div>
      </div>
    </div>

    <button
      type="button"
      onClick={() => onDownload(item)}
      aria-label={`Download ${item.name}`}
      className="shrink-0 flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-xl bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-300 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 border border-indigo-200 dark:border-indigo-800 transition-[background-color,border-color,color,box-shadow] group-hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
    >
      <SafeIcon icon={FiDownload} />
      <span className="hidden sm:inline">Download</span>
    </button>
  </motion.div>
);

interface ResultsPanelProps {
  converted: ConvertedFile[];
  onDownloadSingle: (item: ConvertedFile) => void | Promise<void>;
  onDownloadAll: () => void | Promise<void>;
}

const ResultsPanel = ({ converted, onDownloadSingle, onDownloadAll }: ResultsPanelProps) => (
  <div className="glass rounded-3xl p-5 md:p-6 shadow-xl">
    {/* Header */}
    <div className="flex items-center justify-between mb-5">
      <div>
        <h3 className="text-sm font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest">
          Results · {converted.length} file{converted.length > 1 ? 's' : ''}
        </h3>
      </div>
      {converted.length > 1 && (
        <button
          type="button"
          onClick={onDownloadAll}
          className="flex items-center gap-2 bg-gradient-to-r from-indigo-600 to-cyan-600 hover:from-indigo-700 hover:to-cyan-700 text-white text-sm font-bold px-5 py-2.5 rounded-xl transition-[background-color,box-shadow,transform] shadow-lg shadow-indigo-500/20 active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-950"
        >
          <SafeIcon icon={FiDownloadCloud} />
          Download All (.zip)
        </button>
      )}
    </div>

    {/* Grid */}
    <div className="max-h-[420px] overflow-y-auto space-y-2 pr-1">
      {converted.map((item, idx) => (
        <ResultCard
          key={item.name + idx}
          item={item}
          onDownload={onDownloadSingle}
          index={idx}
        />
      ))}
    </div>

    {/* Single file download-all shortcut */}
    {converted.length === 1 && (
      <p className="text-center text-xs text-slate-400 mt-4">
        Click <strong>Download</strong> above to save your converted image.
      </p>
    )}
  </div>
);

export default ResultsPanel;
